// src/components/Sidebar.js

import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FiGrid, FiHome, FiSettings, FiChevronLeft, FiArchive } from 'react-icons/fi';
import { FaHardHat, FaTicketAlt } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';

const allSidebarItems = [
  { name: 'Dashboard', icon: <FiGrid />, path: '/' },
  
  // ✅ Explicitly deny Client role on these items
  { name: 'Communities', icon: <FiHome />, path: '/customers', roles: ['User', 'Admin', 'Giga Admin'] },
  { name: 'Tickets', icon: <FaTicketAlt />, path: '/tickets', roles: ['User', 'Admin', 'Giga Admin'] },
  { name: 'Technicians', icon: <FaHardHat />, path: '/technicians', roles: ['User', 'Admin', 'Giga Admin'] },
  
  // ✅ Inventory Link (Always Visible)
  { 
    name: 'Inventory', 
    icon: <FiArchive />, 
    path: '/inventory', 
    roles: ['User', 'Admin', 'Giga Admin'] 
  },

  { name: 'Settings', icon: <FiSettings />, path: '/settings' },
];

const Sidebar = () => { 
  const { user } = useContext(AuthContext);
  const [isDesktopCollapsed, setDesktopCollapsed] = useState(true);
  const [highlighterStyle, setHighlighterStyle] = useState({ top: 0, height: 0, opacity: 0 });
  
  const navRef = useRef(null);
  const location = useLocation();

  const isCollapsed = isDesktopCollapsed; 

  const sidebarItems = useMemo(() => allSidebarItems.filter(item => {
    // ✅ REMOVED: The check for settings.inventoryEnabled is gone.
    // The link will now show as long as the user has the correct role.

    if (!item.roles) return true;
    if (!user?.roles) return false;
    
    // ✅ CLIENT LOGIC: If the item has specific roles, check if user matches any.
    return item.roles.some(role => user.roles.includes(role));
  }), [user]);

  const toggleSidebar = () => {
    setDesktopCollapsed(prev => !prev);
  };

  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;
    const activeLink = navElement.querySelector('.sidebar-item.active');
    if (activeLink) {
      setTimeout(() => {
        const top = activeLink.offsetTop;
        const height = activeLink.offsetHeight;
        setHighlighterStyle({ top, height, opacity: 1 });
      }, 50);
    } else {
      setHighlighterStyle(prev => ({ ...prev, opacity: 0 }));
    }
  }, [location, isCollapsed, sidebarItems]);

  return (
    <aside className={`sidebar ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="sidebar-header">
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          <FiChevronLeft size={20} />
        </button>
      </div>
      <nav className="sidebar-nav" ref={navRef}>
        <div className="sidebar-highlighter" style={highlighterStyle} />
        {sidebarItems.map((item) => (
          <NavLink 
            key={item.name} 
            to={item.path} 
            className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
            data-tooltip={item.name}
          >
            <span className="sidebar-icon">{item.icon}</span>
            <span className="sidebar-item-text">{item.name}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        <span className="version-text">Beta 0.1</span>
      </div>
    </aside>
  );
};

export default Sidebar;